import React, {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  forwardRef,
  ForwardRefExoticComponent,
  ReactElement,
} from 'react';
import classNames from 'classnames';
import { Button as USWDSButton, Icon } from '@ver-uds/uswds-react';

type USWDSButtonProps = ComponentPropsWithRef<typeof USWDSButton>;

type IconProps = ComponentPropsWithoutRef<typeof Icon>;

export interface ButtonProps extends Omit<USWDSButtonProps, 'variant' | 'size'> {
  iconLeft?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  variant?: 'primary' | 'secondary' | 'submit' | 'destructive' | 'inverse' | 'tertiary';
  size?: 'small';
}

const getVariant = (variant?: ButtonProps['variant']): USWDSButtonProps['variant'] => {
  switch (variant) {
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'outline';
    case 'submit':
      return 'accent-cool';
    case 'destructive':
      return 'secondary';
    case 'inverse':
      return 'outline-inverse';
    case 'tertiary':
      return 'unstyled';
    case undefined:
    default:
      return undefined;
  }
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, iconLeft, iconRight, className, variant, size, ...rest }, ref): JSX.Element => (
    <USWDSButton
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      ref={ref}
      className={classNames('ver-button', { 'ver-button--small': size === 'small' }, className)}
      variant={getVariant(variant)}
    >
      {iconLeft ? <div className="ver-button__icon ver-button__icon-left">{iconLeft}</div> : null}
      {children}
      {iconRight ? <div className="ver-button__icon ver-button__icon-right">{iconRight}</div> : null}
    </USWDSButton>
  ),
) as ForwardRefExoticComponent<ButtonProps | HTMLButtonElement>;

export default Button;
